'use client'

import * as React from 'react'
import { usePathname } from '@repo/nextjs-utils/navigation'

interface RouteChangeListenerContextType {
  shouldSkipAmplitudePageViewTracking: boolean
  skipNextPageViewRef: React.MutableRefObject<boolean>
}

const RouteChangeListenerContext = React.createContext<RouteChangeListenerContextType>({
  shouldSkipAmplitudePageViewTracking: false,
  skipNextPageViewRef: { current: false },
})

export const RouteChangeListenerProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [shouldSkipAmplitudePageViewTracking, setShouldSkipAmplitudePageViewTracking] =
    React.useState<boolean>(false)
  const skipNextPageViewRef = React.useRef(false)

  const pathname = usePathname()

  React.useEffect(() => {
    if (skipNextPageViewRef.current) {
      setShouldSkipAmplitudePageViewTracking(true)
      skipNextPageViewRef.current = false
    } else {
      // Reset the skip when the pathname changes
      setShouldSkipAmplitudePageViewTracking(false)
    }
  }, [pathname, skipNextPageViewRef])

  const value = {
    shouldSkipAmplitudePageViewTracking,
    skipNextPageViewRef,
  }

  return (
    <RouteChangeListenerContext.Provider value={value}>
      {children}
    </RouteChangeListenerContext.Provider>
  )
}

export const useRouteChangeListener = (): RouteChangeListenerContextType => {
  const context = React.useContext(RouteChangeListenerContext)

  if (context === undefined) {
    throw new Error('useRouteChangeListener must be used within a RouteChangeListenerProvider')
  }

  return context
}

export const SkipAmplitudePageViewTracking = () => {
  const { skipNextPageViewRef } = useRouteChangeListener()

  React.useEffect(() => {
    skipNextPageViewRef.current = true
  }, [skipNextPageViewRef])

  return null
}
